// Enhanced Zoom Modal with Zoom Controls and Swipe Navigation
class EnhancedZoomModal {
  constructor() {
    this.currentModalIndex = 0;
    this.totalImages = 0;
    this.isZoomed = false;
    this.isDragging = false;
    this.isTransitioning = false;
    this.position = { x: 0, y: 0 };
    this.velocity = { x: 0, y: 0 };
    this.lastPointer = { x: 0, y: 0, time: 0 };
    this.animationId = null;
    this.thumbnailsGenerated = false;
    this.instructionTimer = null;
    this.currentZoomLevel = 1;
    this.minZoom = 1;
    this.maxZoom = 5;
    this.zoomStep = 0.5;
    this.imageSet = [];
    // Pinch zoom properties
    this.initialPinchDistance = 0;
    this.initialZoomLevel = 1;
    this.pinchCenter = { x: 0, y: 0 };
    this.isPinching = false;
    this.touches = [];

    // Enhanced Swipe navigation properties for mobile
    this.swipeStartX = 0;
    this.swipeStartY = 0;
    this.swipeStartTime = 0;
    this.isSwipeDetected = false;
    this.swipeThreshold = 50; // Minimum distance for swipe
    this.swipeTimeThreshold = 500; // Maximum time for swipe (ms) - increased for better detection
    this.swipeVelocityThreshold = 0.2; // Minimum velocity for swipe - reduced for easier detection
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    this.swipeInProgress = false;

    // Performance constants
    this.FRICTION = 0.85;
    this.BOUNCE_DAMPING = 0.2;
    this.MIN_VELOCITY = 0.2;
    this.DRAG_SENSITIVITY = 0.5;
    this.INITIAL_ZOOM_SCALE = 3.0;

    this.init();
  }

  init() {
    this.createModalHTML();
    this.setupEventListeners();
    this.setupProductImages();
  }

  createModalHTML() {
    if (document.getElementById('enhancedZoomModal')) return;

    const modalHTML = `
<div id="enhancedZoomModal" class="zoom-modal">
  <div class="zoom-backdrop"></div>
  <div class="zoom-container">
    <!-- Close Button -->
    <button class="zoom-btn zoom-close" onclick="zoomModal.close()" aria-label="Close">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>
      <button class="zoom-btn zoom-nav zoom-prev" onclick="zoomModal.navigate(-1)" aria-label="Previous">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="15,18 9,12 15,6"></polyline>
      </svg>
    </button>
    <button class="zoom-btn zoom-nav zoom-next" onclick="zoomModal.navigate(1)" aria-label="Next">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="9,18 15,12 9,6"></polyline>
      </svg>
    </button> 
    <!-- Counter -->
    <div class="zoom-counter">
      <span id="zoomCounter">1 / 1</span>
    </div>
    
    <!-- Instructions -->
    
    <style>
       .zoom-instructions{
       top:20px;
       left:15%;
       }
    </style>
    <!-- Main Image -->
    <div class="zoom-image-container">
      <img id="enhancedZoomedImage" class="zoom-image" alt="Product Image" style="display: block;">
    </div>
    
    <!-- Zoom Controls and Thumbnails Container -->
    <div class="zoom-controls-container">
      <!-- Zoom Controls -->
      <button id="zoomOutBtn" class="zoom-control-btn" onclick="zoomModal.zoomOut()" aria-label="Zoom Out">−</button>
      <button id="zoomInBtn" class="zoom-control-btn" onclick="zoomModal.zoomIn()" aria-label="Zoom In">+</button>
      
      <!-- Thumbnails -->
      <div class="zoom-thumbnails" id="zoomThumbnails"></div>
    </div>
  </div>
</div>
`;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
  }

  setupEventListeners() {
    document.addEventListener('keydown', (e) => this.handleKeyboard(e));
    document.addEventListener('contextmenu', (e) => {
      if (this.isZoomed && e && e.preventDefault && e.cancelable) e.preventDefault();
    });
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('zoom-backdrop')) {
        this.close();
      }
    });
  }

  setupProductImages() {
    // Only add click handlers to desktop carousel images since that's what we show in the modal
    const productImages = Array.from(
      document.querySelectorAll('.carousel-image')
    );
    this.totalImages = productImages.length;

    if (this.totalImages === 0) return;

    productImages.forEach((img, index) => {
      // Skip detail images from being clickable
      const isDetailImage = img.hasAttribute('data-detail-image') || 
                           img.closest('.carousel-cell')?.hasAttribute('data-no-zoom');
      
      if (!isDetailImage) {
        img.style.cursor = 'zoom-in';
        img.removeEventListener('click', this.handleProductImageClick);
        img.addEventListener('click', () => this.open(index));
      } else {
        img.style.cursor = 'default';
      }
    });

    if (!this.thumbnailsGenerated) {
      this.generateThumbnails(productImages);
      this.thumbnailsGenerated = true;
    }
  }

  generateThumbnails(productImages) {
    const thumbnailsContainer = document.getElementById('zoomThumbnails');
    if (!thumbnailsContainer) return;

    thumbnailsContainer.innerHTML = '';

    // Always use desktop carousel images in the zoom modal (both mobile and desktop)
    const cellSelector = '.carousel-cell';
    const imgSelector = 'img.carousel-image';
    const allCells = document.querySelectorAll(cellSelector);
    const uniqueImages = [];

    allCells.forEach((cell, index) => {
      const img = cell.querySelector(imgSelector);
      const video = cell.querySelector('video');

      // Skip video cells entirely
      if (video) {
        return;
      }

      if (img) {
        // Check if image has a valid src
        if (!img.src || img.src.trim() === '') {
          return;
        }
        
        // Add all images with valid src
        if (!uniqueImages.find((ui) => ui.src === img.src)) {
          const noZoom = img.hasAttribute('data-detail-image') || cell.hasAttribute('data-no-zoom');
          uniqueImages.push({ src: img.src, index, noZoom });
        }
      }
    });

    // Use ALL unique images/videos
    this.imageSet = uniqueImages;

    // Create thumbnails for images only
    uniqueImages.forEach((imgData, index) => {
      const thumb = document.createElement('img');
      thumb.src = imgData.src;
      thumb.className = 'zoom-thumbnail';
      thumb.setAttribute('data-index', index);
      thumb.addEventListener('click', (e) => {
        e.stopPropagation();
        this.goToImage(index);
      });

      thumbnailsContainer.appendChild(thumb);
    });
  }

  open(index = 0) {
    if (this.isTransitioning) return;

    const modal = document.getElementById('enhancedZoomModal');
    const zoomedImage = document.getElementById('enhancedZoomedImage');
    const instructions = document.getElementById('zoomInstructions');

    if (!modal || !zoomedImage) return;
    
    // Don't open modal if there are no valid images
    if (!this.imageSet || this.imageSet.length === 0) {
      console.log('No images available for zoom modal');
      return;
    }
    
    // Make sure the index is valid
    if (index >= this.imageSet.length) {
      index = 0;
    }
    
    this.totalImages = this.imageSet.length;
    this.currentModalIndex = index;
    this.isZoomed = false;
    this.currentZoomLevel = 1;
    this.position = { x: 0, y: 0 };
    this.velocity = { x: 0, y: 0 };

    if (this.instructionTimer) {
      clearTimeout(this.instructionTimer);
    }

    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      modal.classList.add('active');
    });

    this.updateImage();
    this.setupImageInteraction();
    this.updateZoomControls();

    if (instructions) {
      instructions.classList.remove('hidden');
      this.instructionTimer = setTimeout(() => {
        instructions.classList.add('hidden');
      }, 5000);
    }
  }

  close() {
    const modal = document.getElementById('enhancedZoomModal');
    if (!modal) return;

    if (this.instructionTimer) {
      clearTimeout(this.instructionTimer);
      this.instructionTimer = null;
    }

    modal.classList.remove('active');

    setTimeout(() => {
      modal.style.display = 'none';
      document.body.style.overflow = 'auto';
      this.cleanup();
    }, 400);
  }

  navigate(direction) {
    if (this.isTransitioning) return;

    this.isTransitioning = true;
    this.currentModalIndex += direction;

    if (this.currentModalIndex >= this.imageSet.length) {
      this.currentModalIndex = 0;
    } else if (this.currentModalIndex < 0) {
      this.currentModalIndex = this.imageSet.length - 1;
    }

    this.resetZoom();
    this.updateImage();

    setTimeout(() => {
      this.isTransitioning = false;
    }, 300);
  }

  goToImage(index) {
    if (this.isTransitioning || index === this.currentModalIndex) return;

    this.isTransitioning = true;
    this.currentModalIndex = index;
    this.resetZoom();
    this.updateImage();

    setTimeout(() => {
      this.isTransitioning = false;
    }, 300);
  }

  // Zoom In Function
  zoomIn() {
    if (this.currentZoomLevel < this.maxZoom) {
      this.currentZoomLevel += this.zoomStep;
      this.isZoomed = true;
      this.updateImageTransform();
      this.updateZoomControls();

      const zoomedImage = document.getElementById('enhancedZoomedImage');
      if (zoomedImage) {
        zoomedImage.classList.add('zoomed');
      }

      // Hide instructions when zooming
      const instructions = document.getElementById('zoomInstructions');
      if (instructions) {
        instructions.classList.add('hidden');
        if (this.instructionTimer) {
          clearTimeout(this.instructionTimer);
          this.instructionTimer = null;
        }
      }
    }
  }

  // Zoom Out Function
  zoomOut() {
    if (this.currentZoomLevel > this.minZoom) {
      this.currentZoomLevel -= this.zoomStep;

      if (this.currentZoomLevel <= this.minZoom) {
        this.currentZoomLevel = this.minZoom;
        this.isZoomed = false;
        this.position = { x: 0, y: 0 };

        const zoomedImage = document.getElementById('enhancedZoomedImage');
        if (zoomedImage) {
          zoomedImage.classList.remove('zoomed');
        }
      }

      this.updateImageTransform();
      this.updateZoomControls();
    }
  }

  // Update Zoom Control Buttons
  updateZoomControls() {
    const zoomInBtn = document.getElementById('zoomInBtn');
    const zoomOutBtn = document.getElementById('zoomOutBtn');

    if (zoomInBtn) {
      zoomInBtn.disabled = this.currentZoomLevel >= this.maxZoom;
    }

    if (zoomOutBtn) {
      zoomOutBtn.disabled = this.currentZoomLevel <= this.minZoom;
    }
  }

  updateImage() {
    const productImages = this.imageSet;
    const zoomedImage = document.getElementById('enhancedZoomedImage');
    const counter = document.getElementById('zoomCounter');

    if (!productImages[this.currentModalIndex] || !zoomedImage) return;

    const imgData = this.imageSet[this.currentModalIndex];

    const fullSize = imgData.src.replace(/(\d+x\d+)/, '1800x2700');

    zoomedImage.classList.add('transitioning');
    zoomedImage.src = fullSize;

    zoomedImage.onload = () => {
      zoomedImage.classList.remove('transitioning');
    };

    if (counter) {
      counter.textContent = `${this.currentModalIndex + 1} / ${this.imageSet.length}`;
    }

    this.updateThumbnails();
  }

  updateThumbnails() {
    const thumbnails = document.querySelectorAll('.zoom-thumbnail');
    thumbnails.forEach((thumb, index) => {
      thumb.classList.toggle('active', index === this.currentModalIndex);
    });
  }

  setupImageInteraction() {
    const zoomedImage = document.getElementById('enhancedZoomedImage');
    if (!zoomedImage) return;

    zoomedImage.removeEventListener('click', this.handleImageClick);
    zoomedImage.removeEventListener('pointerdown', this.handlePointerDown);

    zoomedImage.addEventListener('click', (e) => this.handleImageClick(e));
    zoomedImage.addEventListener('pointerdown', (e) => this.handlePointerDown(e));

    document.addEventListener('pointermove', (e) => this.handlePointerMove(e));
    document.addEventListener('pointerup', (e) => this.handlePointerUp(e));

    // Add touch event listeners for pinch-to-zoom and swipe navigation
    // Using passive listeners to avoid scroll intervention warnings
    zoomedImage.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
    zoomedImage.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: true });
    zoomedImage.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
  }

  handleImageClick = (e) => {
    if (e && e.preventDefault && e.cancelable) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();

    // Don't interfere with dragging or swiping
    if (this.isDragging || this.swipeInProgress) return;

    // Check if current image should not zoom (detail image)
    const currentImageData = this.imageSet[this.currentModalIndex];
    if (currentImageData && currentImageData.noZoom) {
      // Don't zoom for detail images
      return;
    }

    // Only zoom in if not already zoomed
    if (!this.isZoomed) {
      this.currentZoomLevel = this.INITIAL_ZOOM_SCALE;
      this.isZoomed = true;

      const zoomedImage = document.getElementById('enhancedZoomedImage');
      if (zoomedImage) {
        zoomedImage.classList.add('zoomed');
      }

      this.updateImageTransform();
      this.updateZoomControls();

      // Hide instructions
      const instructions = document.getElementById('zoomInstructions');
      if (instructions) {
        instructions.classList.add('hidden');
        if (this.instructionTimer) {
          clearTimeout(this.instructionTimer);
          this.instructionTimer = null;
        }
      }
    }
  };
  handlePointerDown = (e) => {
    if (!this.isZoomed) return;

    this.isDragging = true;
    const zoomedImage = document.getElementById('enhancedZoomedImage');

    this.lastPointer = {
      x: e.clientX,
      y: e.clientY,
      time: Date.now(),
    };

    this.velocity = { x: 0, y: 0 };

    if (zoomedImage) {
      zoomedImage.classList.add('dragging');
    }

    if (e && e.preventDefault && e.cancelable) e.preventDefault();
  };

  handlePointerMove = (e) => {
    if (!this.isDragging || !this.isZoomed) return;

    const now = Date.now();
    const deltaTime = now - this.lastPointer.time;

    if (deltaTime > 0) {
      this.velocity.x = ((e.clientX - this.lastPointer.x) / deltaTime) * 16 * this.DRAG_SENSITIVITY;
      this.velocity.y = ((e.clientY - this.lastPointer.y) / deltaTime) * 16 * this.DRAG_SENSITIVITY;
    }

    const deltaX = (e.clientX - this.lastPointer.x) * this.DRAG_SENSITIVITY;
    const deltaY = (e.clientY - this.lastPointer.y) * this.DRAG_SENSITIVITY;

    this.position.x += deltaX;
    this.position.y += deltaY;

    this.applyBoundaries();
    this.updateImageTransform();

    this.lastPointer = { x: e.clientX, y: e.clientY, time: now };
    if (e && e.preventDefault && e.cancelable) e.preventDefault();
  };

  handlePointerUp = () => {
    if (!this.isDragging) return;

    this.isDragging = false;
    const zoomedImage = document.getElementById('enhancedZoomedImage');

    if (zoomedImage) {
      zoomedImage.classList.remove('dragging');
    }

    if (
      this.isZoomed &&
      (Math.abs(this.velocity.x) > this.MIN_VELOCITY || Math.abs(this.velocity.y) > this.MIN_VELOCITY)
    ) {
      this.startMomentumAnimation();
    }
  };

  // ENHANCED: Touch event handlers with improved swipe navigation for mobile
  handleTouchStart = (e) => {
    this.touches = Array.from(e.touches);

    if (this.touches.length === 1) {
      // Single touch - could be tap, drag, or swipe
      const touch = this.touches[0];
      this.swipeStartX = touch.clientX;
      this.swipeStartY = touch.clientY;
      this.swipeStartTime = Date.now();
      this.isSwipeDetected = false;
      this.swipeInProgress = false;

      // If zoomed, prepare for dragging
      if (this.isZoomed) {
        this.handlePointerDown({
          clientX: touch.clientX,
          clientY: touch.clientY,
        });
      }
    } else if (this.touches.length === 2) {
      // Two touches - pinch gesture
      this.isPinching = true;
      this.isDragging = false;
      this.isSwipeDetected = false;
      this.swipeInProgress = false;

      const touch1 = this.touches[0];
      const touch2 = this.touches[1];

      this.initialPinchDistance = this.getDistance(touch1, touch2);
      this.initialZoomLevel = this.currentZoomLevel;

      this.pinchCenter = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };

      // preventDefault removed - using passive listeners
    }
  };

  handleTouchMove = (e) => {
    this.touches = Array.from(e.touches);

    if (this.touches.length === 2 && this.isPinching) {
      // Handle pinch zoom
      const touch1 = this.touches[0];
      const touch2 = this.touches[1];

      const currentDistance = this.getDistance(touch1, touch2);
      const scale = currentDistance / this.initialPinchDistance;

      let newZoomLevel = this.initialZoomLevel * scale;
      newZoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, newZoomLevel));

      this.currentZoomLevel = newZoomLevel;

      if (this.currentZoomLevel > this.minZoom) {
        this.isZoomed = true;
        const zoomedImage = document.getElementById('enhancedZoomedImage');
        if (zoomedImage) {
          zoomedImage.classList.add('zoomed');
        }
      }

      const currentCenter = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };

      const centerDelta = {
        x: currentCenter.x - this.pinchCenter.x,
        y: currentCenter.y - this.pinchCenter.y,
      };

      this.position.x += centerDelta.x * 0.5;
      this.position.y += centerDelta.y * 0.5;

      this.applyBoundaries();
      this.updateImageTransform();
      this.updateZoomControls();

      this.pinchCenter = currentCenter;
      // preventDefault removed - using passive listeners
    } else if (this.touches.length === 1 && this.isMobile) {
      // Enhanced single touch movement for mobile
      const touch = this.touches[0];
      const deltaX = touch.clientX - this.swipeStartX;
      const deltaY = touch.clientY - this.swipeStartY;
      const deltaTime = Date.now() - this.swipeStartTime;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // Enhanced swipe detection for mobile navigation (only when not zoomed)
      if (!this.isZoomed && !this.isSwipeDetected && !this.isPinching) {
        // Check if movement is primarily horizontal and meets threshold
        const isHorizontalSwipe = absDeltaX > absDeltaY * 1.5; // Less strict ratio for better detection
        const hasMinimumDistance = absDeltaX > this.swipeThreshold * 0.7; // Lower threshold for easier detection
        const isWithinTimeLimit = deltaTime < this.swipeTimeThreshold;

        if (isHorizontalSwipe && hasMinimumDistance && isWithinTimeLimit) {
          this.isSwipeDetected = true;
          this.swipeInProgress = true;

          // preventDefault removed - using passive listeners

          // Optional: Add visual feedback for swipe
          const zoomedImage = document.getElementById('enhancedZoomedImage');
          if (zoomedImage) {
            zoomedImage.style.transition = 'transform 0.1s ease-out';
            zoomedImage.style.transform = `translateX(${deltaX * 0.1}px)`;
          }
        }
      }

      // Handle dragging when zoomed (existing functionality)
      if (this.isZoomed && !this.isPinching && !this.isSwipeDetected) {
        this.handlePointerMove({
          clientX: touch.clientX,
          clientY: touch.clientY,
        });
        // preventDefault removed - using passive listeners
      }
    }
  };

  handleTouchEnd = (e) => {
    this.touches = Array.from(e.touches);

    if (this.touches.length < 2) {
      this.isPinching = false;

      if (this.currentZoomLevel <= this.minZoom) {
        this.resetZoom();
      }
    }

    // Enhanced swipe navigation for mobile
    if (this.touches.length === 0 && this.isMobile) {
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - this.swipeStartX;
      const deltaY = touch.clientY - this.swipeStartY;
      const deltaTime = Date.now() - this.swipeStartTime;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);
      const velocity = absDeltaX / deltaTime;

      // Reset image transform if there was visual feedback
      const zoomedImage = document.getElementById('enhancedZoomedImage');
      if (zoomedImage && !this.isZoomed) {
        zoomedImage.style.transition = '';
        zoomedImage.style.transform = '';
      }

      // Enhanced swipe validation for mobile navigation
      if (!this.isZoomed && this.imageSet.length > 1) {
        const isValidHorizontalSwipe = absDeltaX > absDeltaY * 1.2; // Horizontal dominance
        const meetsDistanceThreshold = absDeltaX > this.swipeThreshold;
        const meetsVelocityThreshold = velocity > this.swipeVelocityThreshold;
        const isWithinTimeLimit = deltaTime < this.swipeTimeThreshold;
        const hasMinimumMovement = absDeltaX > 30; // Minimum movement for any swipe

        if (
          isValidHorizontalSwipe &&
          (meetsDistanceThreshold || (meetsVelocityThreshold && hasMinimumMovement)) &&
          isWithinTimeLimit
        ) {
          // Determine swipe direction and navigate
          if (deltaX > 0) {
            // Swipe right - go to previous image
            this.navigate(-1);
          } else {
            // Swipe left - go to next image
            this.navigate(1);
          }

          // Prevent any click events that might follow
          setTimeout(() => {
            this.swipeInProgress = false;
          }, 100);
        } else {
          this.swipeInProgress = false;
        }
      }
    }

    if (this.touches.length === 0) {
      this.handlePointerUp();
      this.isSwipeDetected = false;

      // Reset swipe state after a delay
      setTimeout(() => {
        this.swipeInProgress = false;
      }, 50);
    }

    // preventDefault removed - using passive listeners
  };

  getDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  applyBoundaries() {
    const maxX = window.innerWidth * 0.4;
    const maxY = window.innerHeight * 0.45;

    this.position.x = Math.max(-maxX, Math.min(maxX, this.position.x));
    this.position.y = Math.max(-maxY, Math.min(maxY, this.position.y));
  }

  updateImageTransform() {
    const zoomedImage = document.getElementById('enhancedZoomedImage');
    if (!zoomedImage) return;

    if (this.isZoomed || this.currentZoomLevel > this.minZoom) {
      zoomedImage.style.transform = `scale(${this.currentZoomLevel}) translate(${this.position.x}px, ${this.position.y}px)`;
    } else {
      zoomedImage.style.transform = 'scale(1) translate(0px, 0px)';
    }
  }

  startMomentumAnimation() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    const animate = () => {
      if (!this.isZoomed) return;

      this.velocity.x *= this.FRICTION;
      this.velocity.y *= this.FRICTION;

      this.position.x += this.velocity.x;
      this.position.y += this.velocity.y;

      const maxX = window.innerWidth * 0.4;
      const maxY = window.innerHeight * 0.45;

      if (this.position.x > maxX) {
        this.position.x = maxX;
        this.velocity.x *= -this.BOUNCE_DAMPING;
      } else if (this.position.x < -maxX) {
        this.position.x = -maxX;
        this.velocity.x *= -this.BOUNCE_DAMPING;
      }

      if (this.position.y > maxY) {
        this.position.y = maxY;
        this.velocity.y *= -this.BOUNCE_DAMPING;
      } else if (this.position.y < -maxY) {
        this.position.y = -maxY;
        this.velocity.y *= -this.BOUNCE_DAMPING;
      }

      this.updateImageTransform();

      if (Math.abs(this.velocity.x) > this.MIN_VELOCITY || Math.abs(this.velocity.y) > this.MIN_VELOCITY) {
        this.animationId = requestAnimationFrame(animate);
      }
    };

    animate();
  }

  resetZoom() {
    this.isZoomed = false;
    this.currentZoomLevel = 1;
    this.position = { x: 0, y: 0 };
    this.velocity = { x: 0, y: 0 };
    this.isDragging = false;
    this.isPinching = false;
    this.isSwipeDetected = false;
    this.swipeInProgress = false;

    const zoomedImage = document.getElementById('enhancedZoomedImage');
    if (zoomedImage) {
      zoomedImage.classList.remove('zoomed', 'dragging');
      zoomedImage.style.transition = '';
      zoomedImage.style.transform = '';
      this.updateImageTransform();
    }

    this.updateZoomControls();

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  handleKeyboard(e) {
    const modal = document.getElementById('enhancedZoomModal');
    if (!modal || !modal.classList.contains('active')) return;

    switch (e.key) {
      case 'ArrowLeft':
        this.navigate(-1);
        break;
      case 'ArrowRight':
        this.navigate(1);
        break;
      case 'Escape':
        this.close();
        break;
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
      case '_':
        this.zoomOut();
        break;
    }
  }

  cleanup() {
    this.resetZoom();

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    if (this.instructionTimer) {
      clearTimeout(this.instructionTimer);
      this.instructionTimer = null;
    }
  }
}

// Initialize the zoom modal
let zoomModal;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    zoomModal = new EnhancedZoomModal();
  });
} else {
  zoomModal = new EnhancedZoomModal();
}

// Global functions for backward compatibility
window.openZoomModal = (index) => zoomModal?.open(index);
window.closeZoomModal = () => zoomModal?.close();
window.changeModalImage = (direction) => zoomModal?.navigate(direction);
